import type { TaskListTab } from "@octopusdeploy/octopus-server-client";
interface ActionField {
    "Action Type": string;
}
interface ActionMetaField {
    "Action Meta"?: string;
}
interface DocumentUrlField {
    "Document URL"?: string;
}
interface DurationField {
    "Duration (ms)": string;
}
interface ErrorMessageField {
    "Error Messages"?: string;
}
interface LinkLocationField {
    "Link Location": string;
}
interface LinkLabelField {
    "Link Label": string;
}
interface OnboardingField {
    "Onboarding Type": string;
}
interface SampleProjectField {
    "Sample Project Reference": string;
    Source?: "Help Sidebar" | "Welcome Dialog";
}
interface SampleProjectTourStepIndexField {
    "Sample Tour Step Index": number;
}
interface SampleProjectTourStepTitleField {
    "Sample Tour Step Title": string;
}
interface SampleProjectTourStepTargetField {
    "Sample Tour Step Target": string;
}
interface SampleProjectTourStateField {
    "Sample Tour State": string;
}
interface PageAreaField {
    "Page Area": string;
}
interface PageField {
    "Page Id"?: string;
}
export interface PageFields extends PageAreaField, PageField {
}
interface ResourceField {
    "Resource Type": string;
}
interface StatusField {
    Status: string;
}
interface ContextField {
    Context: string;
}
interface StepCategoryField {
    "Step Category": string;
}
interface StepTemplateField {
    "Step Template"?: string;
}
interface TargetField {
    "Target Type"?: string;
}
interface GitRefType {
    "Git Ref Type"?: string;
}
interface IsCaCenabledField {
    "Is CaC Enabled"?: string;
}
interface IsDefaultBranchField {
    "Is Default Branch"?: string;
}
interface CommitMessageField {
    "Commit Message"?: string;
}
interface ActionResultField {
    Status?: string;
}
interface LogoField {
    "Logo Type": string;
}
interface PagingField {
    "Items Per Page": number;
}
interface FilterField {
    "Number of Filter Rules": number;
    "Number of Unique Operators": number;
}
interface DataTableExpansionField {
    Category: string;
}
interface ConnectTenantsField {
    "Number of Tenants": number;
}
interface TenantsWizardField {
    Step: string;
}
interface ProjectField {
    "Project Id"?: string;
}
interface StepIdField {
    "Step Id"?: string;
}
interface InsightsInputField {
    "Insights Input Field"?: string;
}
interface InsightsCadenceField {
    Cadence?: string;
}
interface CloudProviderField {
    "Cloud Provider": string;
}
interface InitialCommitBranchField {
    "Initial Commit Branch": boolean;
}
interface ProtectedBranchesField {
    "Protected Branches": boolean;
}
interface IsProtectedBranchField {
    "Is Protected Branch"?: string;
}
interface CommitBranchField {
    "Commit Branch"?: string;
}
interface PlanField {
    Plan?: string;
}
interface SiemProviderField {
    "SIEM Provider"?: string;
}
interface LocationField {
    Location?: string;
}
interface EntityTypeField {
    "Entity Type"?: string;
}
interface CorrelationIdField {
    "Correlation Id"?: string;
}
interface ProcessTemplateIdsField {
    "Process Template Ids"?: string;
}
interface NotificationStateField {
    State?: string;
}
interface DeprecationField {
    Deprecation?: string;
}
interface ViewTaskStateTabField {
    "Task State": TaskListTab | undefined;
}
interface GuidedSetupField {
    Screen?: string;
    Environments?: string;
    "Project Type"?: string;
    "Project Subtype"?: string;
    "Use Version Control"?: string;
    "Target Type"?: string;
    "Source Page"?: string;
    "Agent Name"?: string;
    "Storage Class"?: string;
}
interface KubernetesAgentSetupField {
    "Agent Name"?: string;
    "Storage Class"?: string;
    "Source Page": string;
}
interface ReleaseField {
    "Release Id"?: string;
}
interface EnvironmentField {
    "Environment Id"?: string;
}
interface DeploymentField {
    "Deployment Id"?: string;
}
interface DeploymentStatusField {
    "Deployment Status"?: string;
}
interface LiveStatusObjectField {
    "Object Health"?: string;
    Tab: string;
}
interface LicenseUsageField {
    Resource: string;
    Urgency: string;
}
interface LiveApplicationHealthField {
    "Live Application Health": string;
}
export type LiveInformationSource = (typeof LiveInformationSourceTypes)[keyof typeof LiveInformationSourceTypes];
export const LiveInformationSourceTypes = {
    KubernetesMonitor: "Kubernetes monitor",
    ArgoCD: "Argo CD",
    Unknown: "Unknown",
} as const;
interface LiveApplicationInformationSourceField {
    "Live Information Source": LiveInformationSource[];
}
interface KubernetesDrawerView {
    Tab: string;
}
interface LiveManifestField {
    Type: "live" | "applied" | "diff";
}
interface KubernetesObjectField {
    "Object Id": string;
    "Object Type": string;
    "Object Health"?: string;
    "Object Sync Status": string | null;
}
interface ExecutionTypeField {
    "Execution Type": string;
}
interface KubernetesViewField {
    View: string;
}
interface DiffViewField {
    "Diff View Type": string;
}
interface KubernetesComparedManifestsField {
    "From Deployment Id": string;
    "To Deployment Id": string;
}
interface LiveStatusSwitchField {
    "Switch Enabled": boolean;
}
interface KubernetesMonitorInstallationStatusField {
    "Monitor Enabled": boolean;
    "Monitor Status"?: string;
    "Agent Name": string;
    "Agent Health": string;
    "Storage Class Name": string;
    "Machine Type": string;
}
interface ProcessTemplateField {
    "Process Template Id": string;
}
interface ParametersField {
    Parameters?: string;
}
interface ControlTypeField {
    "Control Type"?: string;
}
interface CommitProcessTemplateField {
    "Commit Type"?: string;
}
interface PublishProcessTemplateField {
    "Release Type"?: string;
    "Is Pre-Release"?: boolean;
}
interface ShareProcessTemplateField {
    "Shared To"?: string;
    "Space Count"?: number;
}
interface ProcessTemplateVersioningPreferenceField {
    "Versioning Preference"?: string;
    "Is Pre-Release"?: boolean;
}
export interface PageFields extends PageField, PageAreaField {
}
export interface ViewFields extends ResourceField, ProjectField {
}
// Fields for an event performed
export interface ActionedFields extends ActionField, PageAreaField, PageField, ResourceField, TargetField, StepTemplateField, ActionResultField, IsDefaultBranchField, GitRefType, ProjectField, CommitMessageField, PlanField, IsCaCenabledField {
}
export interface ArgoCDGatewayNameField {
    "Gateway Name": string;
}
export interface SpaceIdField {
    "Space Id": string;
}
export interface UniqueIdentifierField {
    "Unique Identifier": string;
}
// Fields indicating an Action undertaken
export interface ActionFields extends ActionField, ActionMetaField, DurationField, PageAreaField, PageField, ResourceField, StatusField, TargetField, StepTemplateField, IsCaCenabledField, ProjectField, IsDefaultBranchField, GitRefType, CommitMessageField, IsProtectedBranchField, CommitBranchField, SiemProviderField, LocationField, EntityTypeField, CorrelationIdField, ProcessTemplateIdsField {
}
export interface ActionResultFields extends ActionField, ActionResultField {
}
export interface LinkFields extends DocumentUrlField, LinkLabelField, LinkLocationField, PageAreaField, PageField {
}
export interface StepFields extends ActionField, PageAreaField, PageField, ResourceField, StepCategoryField, StepTemplateField, ProjectField {
}
export interface StepEditorFields extends ActionField, PageAreaField, PageField, ResourceField, StepTemplateField, ProjectField, StepIdField {
}
export interface ViewHelpFields extends PageAreaField, ContextField {
}
export interface OnboardingFields extends OnboardingField, PageAreaField, PageField, LinkLabelField, LinkLocationField {
}
export type SampleProjectFields = SampleProjectField;
export interface SampleProjectTourFields extends ActionField, ProjectField, ResourceField, SampleProjectTourStepIndexField, SampleProjectTourStepTitleField, SampleProjectTourStepTargetField, SampleProjectTourStateField {
}
export interface OtherActionFields extends PageAreaField, PageField {
}
export interface LogoFields extends ActionField, ResourceField, LogoField, ProjectField {
}
export interface PagingFields extends ActionField, ResourceField, DurationField, PagingField {
}
export interface FilterFields extends ActionField, ResourceField, DurationField, FilterField {
}
export interface DataTableExpansionFields extends ActionField, ResourceField, DataTableExpansionField {
}
export interface ConnectTenantsFields extends ActionField, ResourceField, ConnectTenantsField {
}
export interface TenantsWizardFields extends ActionField, ResourceField, TenantsWizardField {
}
export interface InsightsFields extends ActionField, PageAreaField, InsightsInputField, InsightsCadenceField {
}
export interface CloudConnectionsFields extends ActionField, ResourceField, CloudProviderField {
}
export interface DeprecationNotificationFields extends NotificationStateField, DeprecationField {
}
export interface ViewTaskStateTabFields extends ActionField, ResourceField, ViewTaskStateTabField {
}
export interface GuidedSetupFields extends ProjectField, CorrelationIdField, ErrorMessageField, GuidedSetupField {
}
export interface KubernetesAgentSetupFields extends CorrelationIdField, ErrorMessageField, KubernetesAgentSetupField {
}
export interface DeploymentFields extends ProjectField, ReleaseField, EnvironmentField, DeploymentField, DeploymentStatusField {
}
export interface LiveStatusPageFields extends DeploymentFields, LiveApplicationHealthField, LiveApplicationInformationSourceField {
}
export interface LiveStatusObjectFields extends DeploymentFields, LiveStatusObjectField {
}
export interface LicenseUsageFields extends LicenseUsageField {
}
export interface ViewKubernetesAppliedManifestFields extends DeploymentFields, KubernetesViewField, ExecutionTypeField {
}
export interface ViewKubernetesObjectDrawerFields extends DeploymentFields, LiveApplicationHealthField, KubernetesDrawerView, PageField, KubernetesObjectField, ExecutionTypeField {
}
export interface ViewKubernetesObjectDrawerManifestFields extends DeploymentFields, LiveApplicationHealthField, PageField, KubernetesObjectField, ExecutionTypeField, LiveManifestField, DiffViewField {
}
export interface ViewKubernetesAppliedManifestsComparedFields extends DeploymentFields, ExecutionTypeField, KubernetesComparedManifestsField, DiffViewField {
}
export interface LiveStatusSwitchFields extends ProjectField, PageField, LiveStatusSwitchField, LiveApplicationInformationSourceField {
}
export interface KubernetesMonitorInstallationStatusFields extends ProjectField, KubernetesMonitorInstallationStatusField {
}
export interface ArgoCDInstallHelmCommandGeneratedFields extends ArgoCDGatewayNameField, SpaceIdField, UniqueIdentifierField {
}
export interface ProcessTemplateProducerActionFields extends ActionField, ResourceField, StepTemplateField, IsDefaultBranchField, IsProtectedBranchField, CommitMessageField, ProcessTemplateField, ParametersField, ControlTypeField, CommitProcessTemplateField, PublishProcessTemplateField, ShareProcessTemplateField {
}
export interface ProcessTemplateConsumerActionFields extends ActionField, ResourceField, ProcessTemplateField, ProjectField, ProcessTemplateVersioningPreferenceField {
}
